import { LocalizedText, LocalizedElement, sourceAttribute, useSourceLanguage } from "../../i18n/LocalizedCopy";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import WelcomeOfferActions from "./WelcomeOfferActions";
import WelcomeOfferArtwork from "./WelcomeOfferArtwork";
import WelcomeOfferDetails from "./WelcomeOfferDetails";
import WelcomeOfferSuccess from "./WelcomeOfferSuccess";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

function focusableElements(node) {
  if (!node) return [];
  return Array.from(node.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (element) => element.getAttribute("aria-hidden") !== "true"
  );
}

export default function WelcomeOfferModal({ "data-et-copy": __etCopy = "",
  phase,
  offer,
  message,
  onClaim,
  onReconcile,
  onDismiss,
  onDiscover,
  onCloseSuccess,
  onWallet,
  onRewards,
}) {
  useSourceLanguage();
  const dialogRef = useRef(null);
  const titleRef = useRef(null);
  const restoreFocusRef = useRef(null);
  const [reducedMotion, setReducedMotion] = useState(
    () => window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches || false
  );
  const isSuccess = phase === "success";
  const canDismiss = true;

  useEffect(() => {
    const query = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!query) return undefined;
    const onChange = (event) => setReducedMotion(Boolean(event.matches));
    query.addEventListener?.("change", onChange);
    return () => query.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => {
    restoreFocusRef.current = document.activeElement;
    const appShell =
      document.querySelector("[data-welcome-offer-app-shell]") ||
      document.getElementById("root");
    const hadInertAttribute = appShell?.hasAttribute("inert") || false;
    const previousInert = appShell?.inert;
    const previousAriaHidden = appShell?.getAttribute("aria-hidden");
    const previousOverflow = document.body.style.overflow;

    if (appShell) {
      appShell.inert = true;
      appShell.setAttribute("inert", "");
      appShell.setAttribute("aria-hidden", "true");
    }
    document.body.style.overflow = "hidden";
    titleRef.current?.focus();

    return () => {
      if (appShell) {
        appShell.inert = previousInert;
        if (!hadInertAttribute) appShell.removeAttribute("inert");
        if (previousAriaHidden == null) appShell.removeAttribute("aria-hidden");
        else appShell.setAttribute("aria-hidden", previousAriaHidden);
      }
      document.body.style.overflow = previousOverflow;
      const previous = restoreFocusRef.current;
      if (previous && previous.isConnected && typeof previous.focus === "function") {
        previous.focus();
      }
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        if (!canDismiss) return;
        event.preventDefault();
        if (isSuccess) onCloseSuccess();
        else onDismiss("escape");
        return;
      }
      if (event.key !== "Tab") return;
      const elements = focusableElements(dialogRef.current);
      if (!elements.length) {
        event.preventDefault();
        titleRef.current?.focus();
        return;
      }
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (
        event.shiftKey &&
        (document.activeElement === first || document.activeElement === titleRef.current)
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === last || document.activeElement === titleRef.current)
      ) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [canDismiss, isSuccess, onCloseSuccess, onDismiss]);

  const requestClose = () => {
    if (!canDismiss) return;
    if (isSuccess) onCloseSuccess();
    else onDismiss("close");
  };

  return createPortal(
    <div
      className="welcome-offer-root"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        ref={dialogRef}
        className={`welcome-offer-modal welcome-offer-modal--${phase}`}
        data-reduced-motion={reducedMotion ? "true" : "false"}
        role="dialog"
        aria-modal="true"
        aria-labelledby="welcome-offer-title"
        aria-describedby="welcome-offer-description"
        aria-busy={phase === "claiming" ? "true" : undefined}
      >
        <span className="welcome-offer-modal__border" aria-hidden="true" />
        <LocalizedElement elementType={"button"} copyAttributes={["aria-label"]}
          type="button"
          className="welcome-offer-modal__close"
          onClick={requestClose}
          disabled={!canDismiss}
          aria-label={sourceAttribute("Fermer la fenêtre de bienvenue")}
        >
          <span aria-hidden="true">×</span>
        </LocalizedElement>

        <div className="welcome-offer-modal__artwork-column">
          <WelcomeOfferArtwork />
        </div>
        <div className="welcome-offer-modal__content">
          {isSuccess ? (
            <WelcomeOfferSuccess
              ref={titleRef}
              onWallet={onWallet}
              onRewards={onRewards}
            />
          ) : (
            <>
              <WelcomeOfferDetails
                ref={titleRef}
                offer={offer}
                phase={phase}
                message={message}
              />
              <WelcomeOfferActions
                phase={phase}
                offer={offer}
                onClaim={onClaim}
                onReconcile={onReconcile}
                onDiscover={onDiscover}
                onDismiss={() => onDismiss("later")}
              />
            </>
          )}
        </div>

        <p className="welcome-offer-sr-only" role="status" aria-live="polite" aria-atomic="true">
          {isSuccess
            ? <LocalizedText source={"Bonus activé. Ton portefeuille a été actualisé."} />
            : message || (phase === "claiming" ? <LocalizedText source={"Activation en cours."} /> : "")}
        </p>
      </div>
    </div>,
    document.body
  );
}
